import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, IconButton, Button, TextField, MenuItem,
  CircularProgress, Chip
} from '@mui/material';
import { ArrowBack, Add, Delete } from '@mui/icons-material';
import axios from 'axios';
import AttachmentsPanel from './AttachmentsPanel';
import { fmt, sym } from '../utils/currency';
import { useGLAccounts } from '../hooks/useGLAccounts';
import { glAccountOptions } from '../utils/glAccountOptions';
import { useLocations } from '../hooks/useLocations';

const emptyLine = () => ({
  _key:            Date.now() + Math.random(),
  itemNo:          '',
  description:     '',
  qty:             '',
  unitCost:        '',
  amount:          0,
  fromInventoryGL: '',
  toInventoryGL:   ''
});

const TransferOrderForm = () => {
  const navigate       = useNavigate();
  const { transferNo } = useParams();
  const isEdit         = Boolean(transferNo);

  const [header, setHeader] = useState({
    transferDate:     new Date().toISOString().split('T')[0],
    fromLocationCode: '',
    toLocationCode:   '',
    description:      ''
  });
  const [lines,    setLines]    = useState([emptyLine()]);
  const [items,    setItems]    = useState([]);
  const [stockMap, setStockMap] = useState({});
  const [error,    setError]    = useState('');
  const [saving,   setSaving]   = useState(false);
  const [loading,  setLoading]  = useState(true);
  const [status,   setStatus]   = useState('Open');

  const { accounts: glAccounts } = useGLAccounts();
  const { locations }            = useLocations();
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    const load = async () => {
      try {
        const [itemsRes, stockRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/payables/items`,      { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/api/payables/item-stock`, { headers })
        ]);
        setItems(itemsRes.data.filter(i => i.itemType === 'Item'));
        setStockMap(stockRes.data);

        if (isEdit) {
          const { data } = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/procurement/transfers/${transferNo}`,
            { headers }
          );
          setHeader({
            transferDate:     data.transferDate ? data.transferDate.split('T')[0] : '',
            fromLocationCode: data.fromLocationCode || '',
            toLocationCode:   data.toLocationCode   || '',
            description:      data.description      || ''
          });
          setLines(data.lines.map(l => ({ ...l, _key: Date.now() + Math.random() })));
          setStatus(data.status || 'Open');
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [transferNo]); // eslint-disable-line

  // Stock at the selected FROM location for an item
  const stockAtFrom = (itemNo) => {
    const s = stockMap[itemNo];
    if (!s || !header.fromLocationCode) return null;
    return s.byLocation?.[header.fromLocationCode] || { qty: 0, value: 0 };
  };

  const updateLine = (key, field, value) => {
    setLines(prev => prev.map(l => {
      if (l._key !== key) return l;
      const updated = { ...l, [field]: value };

      if (field === 'itemNo') {
        const item = items.find(i => i.itemNo === value);
        if (item) {
          const s       = stockMap[value];
          const avgCost = s && s.qty > 0 ? s.value / s.qty : item.unitPrice || 0;
          updated.description     = item.itemName;
          updated.fromInventoryGL = item.glAccount;
          updated.unitCost        = avgCost.toFixed(4);
          updated.amount          = (parseFloat(updated.qty) || 0) * avgCost;
        }
      }

      if (field === 'qty' || field === 'unitCost') {
        const qty  = parseFloat(field === 'qty'      ? value : updated.qty)      || 0;
        const cost = parseFloat(field === 'unitCost' ? value : updated.unitCost) || 0;
        updated.amount = qty * cost;
      }

      return updated;
    }));
  };

  const addLine    = () => setLines(prev => [...prev, emptyLine()]);
  const removeLine = (key) => setLines(prev => prev.filter(l => l._key !== key));

  const totalAmount = lines.reduce((s, l) => s + (l.amount || 0), 0);

  const handleSave = async () => {
    setError('');
    if (!header.fromLocationCode) { setError('From Location is required'); return; }
    if (!header.toLocationCode)   { setError('To Location is required');   return; }
    if (header.fromLocationCode === header.toLocationCode) {
      setError('From and To locations must be different'); return;
    }
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      if (!l.itemNo)                        { setError(`Line ${i + 1}: Item is required`);              return; }
      if (!l.qty || parseFloat(l.qty) <= 0) { setError(`Line ${i + 1}: Quantity must be > 0`);         return; }
      if (!l.fromInventoryGL)               { setError(`Line ${i + 1}: From Inventory GL is required`); return; }
      if (!l.toInventoryGL)                 { setError(`Line ${i + 1}: To Inventory GL is required`);   return; }
    }

    setSaving(true);
    try {
      const payload = {
        ...header,
        lines: lines.map((l, i) => ({
          lineNo:          i + 1,
          itemNo:          l.itemNo,
          description:     l.description,
          qty:             parseFloat(l.qty),
          unitCost:        parseFloat(l.unitCost) || 0,
          amount:          l.amount || 0,
          fromInventoryGL: l.fromInventoryGL,
          toInventoryGL:   l.toInventoryGL
        })),
        totalAmount
      };

      if (isEdit) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/procurement/transfers/${transferNo}`,
          payload, { headers }
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/procurement/transfers`,
          payload, { headers }
        );
      }
      navigate('/procurement/transfers');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save transfer order');
    } finally {
      setSaving(false);
    }
  };

  const isReadOnly = isEdit && status !== 'Open';

  if (loading) return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading...</Typography>
    </Box>
  );

  const locName = (code) => locations.find(l => l.locationCode === code)?.locationName || code;

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={3}>
            <Button startIcon={<ArrowBack />} onClick={() => navigate('/procurement/transfers')} sx={{ mr: 2 }}>
              Back
            </Button>
            <Typography variant="h5" color="primary">
              {isEdit ? `Edit Transfer Order: ${transferNo}` : 'New Transfer Order'}
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {isReadOnly && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This transfer order is <strong>{status}</strong> and cannot be edited.
            </Alert>
          )}
          {isEdit && (
            <Box mb={3}>
              <AttachmentsPanel refType="TransferOrder" refId={transferNo} />
            </Box>
          )}

          {/* Header */}
          <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
            <TextField label="Transfer Date" type="date" size="small"
              value={header.transferDate}
              onChange={e => setHeader({ ...header, transferDate: e.target.value })}
              disabled={isReadOnly}
              InputLabelProps={{ shrink: true }} sx={{ minWidth: 180 }} />

            <TextField select label="From Location" size="small"
              value={header.fromLocationCode}
              onChange={e => setHeader({ ...header, fromLocationCode: e.target.value })}
              disabled={isReadOnly}
              sx={{ minWidth: 220 }}>
              <MenuItem value=""><em>Select Location</em></MenuItem>
              {locations.map(loc => (
                <MenuItem key={loc.locationCode} value={loc.locationCode}>
                  {loc.locationCode} – {loc.locationName}
                </MenuItem>
              ))}
            </TextField>

            <TextField select label="To Location" size="small"
              value={header.toLocationCode}
              onChange={e => setHeader({ ...header, toLocationCode: e.target.value })}
              disabled={isReadOnly}
              sx={{ minWidth: 220 }}>
              <MenuItem value=""><em>Select Location</em></MenuItem>
              {locations.filter(l => l.locationCode !== header.fromLocationCode).map(loc => (
                <MenuItem key={loc.locationCode} value={loc.locationCode}>
                  {loc.locationCode} – {loc.locationName}
                </MenuItem>
              ))}
            </TextField>

            <TextField label="Description" size="small"
              value={header.description}
              onChange={e => setHeader({ ...header, description: e.target.value })}
              disabled={isReadOnly}
              sx={{ minWidth: 260, flex: 1 }} />
          </Box>

          {/* Lines */}
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Item</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell align="right">
                  <strong>
                    {header.fromLocationCode
                      ? `Stock @ ${locName(header.fromLocationCode)}`
                      : 'Stock at From'}
                  </strong>
                </TableCell>
                <TableCell align="right"><strong>Qty</strong></TableCell>
                <TableCell align="right"><strong>Unit Cost</strong></TableCell>
                <TableCell><strong>From GL (Cr)</strong></TableCell>
                <TableCell><strong>To GL (Dr)</strong></TableCell>
                <TableCell align="right"><strong>Amount</strong></TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map(line => {
                const locStock = stockAtFrom(line.itemNo);
                const availQty = locStock ? locStock.qty : null;
                return (
                  <TableRow key={line._key}>
                    <TableCell sx={{ minWidth: 160 }}>
                      <TextField select size="small" value={line.itemNo} fullWidth
                        disabled={isReadOnly}
                        onChange={e => updateLine(line._key, 'itemNo', e.target.value)}>
                        <MenuItem value=""><em>Select Item</em></MenuItem>
                        {items.map(i => (
                          <MenuItem key={i.itemNo} value={i.itemNo}>
                            {i.itemNo} – {i.itemName}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell sx={{ minWidth: 130 }}>
                      <TextField size="small" value={line.description} fullWidth
                        disabled={isReadOnly}
                        onChange={e => updateLine(line._key, 'description', e.target.value)} />
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 100 }}>
                      {line.itemNo && availQty !== null ? (
                        <Chip
                          label={availQty.toFixed(2)}
                          color={availQty > 0 ? 'success' : 'error'}
                          size="small" variant="outlined"
                          sx={{ fontWeight: 600 }}
                        />
                      ) : line.itemNo ? (
                        <Typography variant="caption" color="text.disabled">
                          {header.fromLocationCode ? '0.00' : 'pick location'}
                        </Typography>
                      ) : ''}
                    </TableCell>
                    <TableCell sx={{ minWidth: 80 }}>
                      <TextField size="small" type="number" value={line.qty} fullWidth
                        disabled={isReadOnly}
                        inputProps={{ min: 0.001, step: '0.001' }}
                        onChange={e => updateLine(line._key, 'qty', e.target.value)} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 100 }}>
                      <TextField size="small" type="number" value={line.unitCost} fullWidth
                        disabled={isReadOnly}
                        inputProps={{ min: 0, step: '0.01' }}
                        onChange={e => updateLine(line._key, 'unitCost', e.target.value)} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 170 }}>
                      <TextField select size="small" value={line.fromInventoryGL} fullWidth
                        disabled={isReadOnly}
                        onChange={e => updateLine(line._key, 'fromInventoryGL', e.target.value)}>
                        {glAccountOptions(glAccounts)}
                      </TextField>
                    </TableCell>
                    <TableCell sx={{ minWidth: 170 }}>
                      <TextField select size="small" value={line.toInventoryGL} fullWidth
                        disabled={isReadOnly}
                        onChange={e => updateLine(line._key, 'toInventoryGL', e.target.value)}>
                        {glAccountOptions(glAccounts)}
                      </TextField>
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 90, fontWeight: 600 }}>
                      {(line.amount || 0).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      {!isReadOnly && (
                        <IconButton size="small" color="error" onClick={() => removeLine(line._key)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <Box display="flex" justifyContent="space-between" alignItems="center">
            {!isReadOnly && (
              <Button startIcon={<Add />} onClick={addLine} variant="outlined" size="small">
                Add Line
              </Button>
            )}
            <Typography variant="h6" fontWeight={700} ml="auto">
              Total: {sym()} {fmt(totalAmount)}
            </Typography>
          </Box>

          <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
            <Button onClick={() => navigate('/procurement/transfers')}>
              {isReadOnly ? 'Back' : 'Cancel'}
            </Button>
            {!isReadOnly && (
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TransferOrderForm;
